import React, { Component } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { withApollo, graphql } from "react-apollo";
import { withRouter } from "react-router-dom";
import compose from "lodash.flowright";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import moment from "moment";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";
import DateBlock from "../home_bck/DateBlock";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ── GraphQL ──────────────────────────────────────────────────────────────────
const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const GET_CLASSES = gql`
  query($id: Int) {
    getExamClasses(id: $id) {
      id
      class
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`
  query getAcademicYears($id: Int) {
    getAcademicYears(id: $id) {
      id
      academic_year
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard aggregate service contract
//
//   Service:  getAdmissionsDashboard
//   Inputs :  institute_id (Int!), branch_id (String — comma-separated IDs), fromDate (String), toDate (String)
//   Returns:  one consolidated payload powering every chart/KPI on this page
//             — see SAMPLE_DASHBOARD_RESPONSE below for the exact shape.
//
// The two list-style sections (Enquiries table, Admissions table) read from
// existing services:
//   - getPaginatedEnquirySubmissions  (already used by the Enquiry List tab)
//   - getAdmissionStudentList         (already used by the Admission List tab)
// ─────────────────────────────────────────────────────────────────────────────
const GET_ADMISSIONS_DASHBOARD = gql`
  query getAdmissionsDashboard(
    $institute_id: Int!,
    $branch_id: String,
    $fromDate: String,
    $toDate: String
  ) {
    getAdmissionsDashboard(
      institute_id: $institute_id,
      branch_id: $branch_id,
      fromDate: $fromDate,
      toDate: $toDate
    ) {
      kpis {
        totalEnquiries
        totalLeads
        totalExams
        totalAdmissions
      }
      dayWise          { date  enquiries  admissions }
      classWise        { name  enquiries  admissions }
      branchWise       { name  enquiries  admissions }
      leadStatus       { hot   warm  cold  converted }
      sourceWise       { name  total      converted   }
      admissionStatus  { confirmed  pendingFee }
      weekdayDistribution { day  enquiries  admissions }
      enquiries  { id  date  name  phone  className  branch  source  status }
      admissions { id  date  name  phone  className  branch  rollNo  status }
    }
  }
`;

// ── Theme ────────────────────────────────────────────────────────────────────
const THEME = {
  bg: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)",
  primary: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#06b6d4",
  purple: "#7c3aed",
  text: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#64748b",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  cardShadow: "0 4px 24px rgba(0,0,0,0.05)",
};

// ── Static sample data (replace with backend feed) ───────────────────────────
const KPI_DATA = [
  { key: "enquiry",   icon: "fal fa-file-alt",      label: "Enquiries",     value: 1245, subtext: "All enquiries",     color: THEME.primary },
  { key: "lead",      icon: "fal fa-tasks",         label: "Lead Tracking", value: 856,  subtext: "Active leads",      color: THEME.purple  },
  { key: "exam",      icon: "fal fa-pencil-alt",    label: "Exams",         value: 489,  subtext: "Tests conducted",   color: THEME.warning },
  { key: "admission", icon: "fal fa-user-graduate", label: "Admissions",    value: 312,  subtext: "Admitted students", color: THEME.success },
];

const SAMPLE_NAMES = [
  "Rahul Sharma", "Priya Patel", "Aman Verma", "Sneha Iyer", "Karthik Reddy", "Meera Kapoor",
  "Yash Khanna", "Arjun Singh", "Divya Nair", "Rohan Joshi", "Anika Bose", "Vikram Rao",
  "Pooja Menon", "Ishaan Gupta", "Neha Desai", "Saurabh Mehta", "Tanvi Shah", "Rajat Pillai",
  "Kavya Krishnan", "Aditya Bhat", "Riya Saxena", "Manish Tiwari", "Shreya Banerjee", "Harsh Pandey",
  "Aarav Malhotra", "Isha Chopra", "Dev Agarwal", "Nikita Rao", "Siddharth Jain", "Aishwarya Kumar",
];
const SAMPLE_CLASSES  = ["Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];
const SAMPLE_BRANCHES = ["Main Campus", "North Branch", "South Branch", "East Branch", "West Branch"];
const SAMPLE_SOURCES  = ["Walk-in", "Website", "Referral", "Phone Call", "Social Media", "Event"];
const ENQUIRY_STATUSES   = ["Hot", "Warm", "Cold", "Converted"];
const ADMISSION_STATUSES = ["Confirmed", "Confirmed", "Confirmed", "Pending Fee"];

const pick = (arr, i) => arr[i % arr.length];

const dayOffset = (offset) => {
  const d = new Date(2026, 4, 26);
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
};

const ENQUIRIES_DATA = Array.from({ length: 60 }, (_, i) => ({
  id:        `ENQ-${i + 1}`,
  date:      dayOffset(i % 30),
  name:      pick(SAMPLE_NAMES, i),
  phone:     `98765${String(43000 + i).padStart(5, "0")}`,
  className: pick(SAMPLE_CLASSES, i * 3),
  branch:    pick(SAMPLE_BRANCHES, i * 7),
  source:    pick(SAMPLE_SOURCES, i * 5),
  status:    pick(ENQUIRY_STATUSES, i * 11),
}));

const ADMISSIONS_DATA = Array.from({ length: 40 }, (_, i) => ({
  id:        `ADM-${i + 1}`,
  date:      dayOffset((i * 2) % 30),
  name:      pick(SAMPLE_NAMES, i + 7),
  phone:     `98762${String(11000 + i).padStart(5, "0")}`,
  className: pick(SAMPLE_CLASSES, i * 2),
  branch:    pick(SAMPLE_BRANCHES, i * 3),
  rollNo:    `ADM-2026-${String(i + 1).padStart(3, "0")}`,
  status:    pick(ADMISSION_STATUSES, i * 4),
}));

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE_DASHBOARD_RESPONSE — what the backend should return from
// `getAdmissionsDashboard`. Used as a fallback when the API isn't reachable
// (e.g. local dev). Built from the deterministic sample arrays above so the
// dashboard always has consistent demo numbers to render.
// ─────────────────────────────────────────────────────────────────────────────
const buildSampleDashboardResponse = () => {
  const countBy = (rows, key) => rows.reduce((acc, r) => { acc[r[key]] = (acc[r[key]] || 0) + 1; return acc; }, {});

  const dayMap = {};
  ENQUIRIES_DATA.forEach(r => { dayMap[r.date] = dayMap[r.date] || { date: r.date, enquiries: 0, admissions: 0 }; dayMap[r.date].enquiries++; });
  ADMISSIONS_DATA.forEach(r => { dayMap[r.date] = dayMap[r.date] || { date: r.date, enquiries: 0, admissions: 0 }; dayMap[r.date].admissions++; });
  const dayWise = Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));

  const allClasses = Array.from(new Set([...ENQUIRIES_DATA.map(r => r.className), ...ADMISSIONS_DATA.map(r => r.className)]));
  const classWise = allClasses.map(c => ({
    name: c,
    enquiries:  ENQUIRIES_DATA.filter(r => r.className === c).length,
    admissions: ADMISSIONS_DATA.filter(r => r.className === c).length,
  })).sort((a, b) => a.name.localeCompare(b.name));

  const allBranches = Array.from(new Set([...ENQUIRIES_DATA.map(r => r.branch), ...ADMISSIONS_DATA.map(r => r.branch)]));
  const branchWise = allBranches.map(b => ({
    name: b,
    enquiries:  ENQUIRIES_DATA.filter(r => r.branch === b).length,
    admissions: ADMISSIONS_DATA.filter(r => r.branch === b).length,
  }));

  const enqStatusCounts = countBy(ENQUIRIES_DATA, "status");
  const leadStatus = {
    hot:       enqStatusCounts.Hot       || 0,
    warm:      enqStatusCounts.Warm      || 0,
    cold:      enqStatusCounts.Cold      || 0,
    converted: enqStatusCounts.Converted || 0,
  };

  const srcCounts = countBy(ENQUIRIES_DATA, "source");
  const sourceWise = Object.entries(srcCounts).map(([name, total]) => ({
    name,
    total,
    converted: ENQUIRIES_DATA.filter(r => r.source === name && r.status === "Converted").length,
  })).sort((a, b) => b.total - a.total);

  const admStatusCounts = countBy(ADMISSIONS_DATA, "status");
  const admissionStatus = {
    confirmed:  admStatusCounts.Confirmed     || 0,
    pendingFee: admStatusCounts["Pending Fee"] || 0,
  };

  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weekdayDistribution = weekdayNames.map((day, i) => ({
    day,
    enquiries:  ENQUIRIES_DATA.filter(r => new Date(r.date).getDay() === i).length,
    admissions: ADMISSIONS_DATA.filter(r => new Date(r.date).getDay() === i).length,
  }));

  return {
    kpis: {
      totalEnquiries:  ENQUIRIES_DATA.length,
      totalLeads:      ENQUIRIES_DATA.filter(r => r.status === "Hot" || r.status === "Warm").length,
      totalExams:      Math.round(ENQUIRIES_DATA.length * 0.55),
      totalAdmissions: ADMISSIONS_DATA.length,
    },
    dayWise,
    classWise,
    branchWise,
    leadStatus,
    sourceWise,
    admissionStatus,
    weekdayDistribution,
    enquiries:  ENQUIRIES_DATA,
    admissions: ADMISSIONS_DATA,
  };
};

const SAMPLE_DASHBOARD_RESPONSE = buildSampleDashboardResponse();

// Empty initial state — every section renders its own "No data" message.
const EMPTY_DASHBOARD_RESPONSE = {
  kpis: { totalEnquiries: 0, totalLeads: 0, totalExams: 0, totalAdmissions: 0 },
  dayWise: [],
  classWise: [],
  branchWise: [],
  leadStatus: { hot: 0, warm: 0, cold: 0, converted: 0 },
  sourceWise: [],
  admissionStatus: { confirmed: 0, pendingFee: 0 },
  weekdayDistribution: [],
  enquiries: [],
  admissions: [],
};

// ── Checkbox Dropdown ────────────────────────────────────────────────────────
class CheckboxDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
    this.ref = React.createRef();
  }
  componentDidMount()    { document.addEventListener("mousedown", this.handleOutside); }
  componentWillUnmount() { document.removeEventListener("mousedown", this.handleOutside); }
  handleOutside = (e) => {
    if (this.ref.current && !this.ref.current.contains(e.target)) this.setState({ open: false });
  };
  toggle = (id) => {
    const { value, onChange } = this.props;
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };
  render() {
    const { options, value, placeholder, onChange } = this.props;
    const { open } = this.state;
    const label = value.length === 0 ? placeholder
      : value.length === options.length ? `All (${options.length})`
      : value.length === 1 ? (options.find(o => o.value === value[0])?.label || `${value.length} selected`)
      : `${value.length} selected`;
    return (
      <div ref={this.ref} style={{ position: "relative" }}>
        <div
          onClick={() => this.setState({ open: !open })}
          style={{
            borderRadius: 10,
            border: "1.5px solid #e5e7eb",
            padding: "9px 14px",
            fontSize: 14,
            color: value.length ? "#1f2937" : "#9ca3af",
            background: "#f9fafb",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            userSelect: "none",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          <i className={`fal fa-chevron-${open ? "up" : "down"}`} style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0, marginLeft: 6 }} />
        </div>
        {open && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 9999, maxHeight: 220, overflowY: "auto" }}>
            {options.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 12, color: "#9ca3af" }}>No options</div>
            ) : (
              <>
                <div
                  onClick={() => onChange(value.length === options.length ? [] : options.map(o => o.value))}
                  style={{ padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#6366f1", cursor: "pointer", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input type="checkbox" readOnly checked={value.length === options.length} style={{ accentColor: "#6366f1" }} />
                  Select All
                </div>
                {options.map(o => (
                  <div
                    key={o.value}
                    onClick={() => this.toggle(o.value)}
                    style={{ padding: "7px 12px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: value.includes(o.value) ? "#f5f3ff" : "transparent", borderBottom: "1px solid #f9fafb" }}
                  >
                    <input type="checkbox" readOnly checked={value.includes(o.value)} style={{ accentColor: "#6366f1" }} />
                    <span style={{ color: value.includes(o.value) ? "#6d28d9" : "#374151", fontWeight: value.includes(o.value) ? 600 : 400 }}>{o.label}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

// ── UI Helpers ───────────────────────────────────────────────────────────────
const IconChip = ({ icon, color = "#7c3aed", bg = "#f5f3ff", border = "#ddd6fe", size = 40 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 12,
      background: bg,
      border: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color,
      fontSize: size * 0.42,
      flexShrink: 0,
    }}
  >
    <i className={icon} />
  </div>
);

const KpiCard = ({ kpi }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderTop: `4px solid ${kpi.color}`,
      borderRadius: 16,
      padding: "18px 20px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      display: "flex",
      alignItems: "center",
      gap: 16,
      height: "100%",
    }}
  >
    <div
      style={{
        background: `${kpi.color}20`,
        color: kpi.color,
        borderRadius: 12,
        width: 50,
        height: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <i className={kpi.icon} style={{ fontSize: 22 }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {kpi.label}
      </div>
      <div style={{ fontSize: 21, fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>
        {kpi.value.toLocaleString()}
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{kpi.subtext}</div>
    </div>
  </div>
);

const Divider = () => <div style={{ height: 1, background: "#f1f5f9", margin: "0" }} />;

const Shimmer = ({ width = "100%", height = 16, radius = 8, style = {} }) => (
  <span className="adm-shimmer" style={{ width, height, borderRadius: radius, display: "block", ...style }} />
);

const ShimmerKpi = () => (
  <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderTop: "4px solid #e5e7eb", borderRadius: 16, padding: "18px 20px", display: "flex", alignItems: "center", gap: 16, height: "100%" }}>
    <Shimmer width={50} height={50} radius={12} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <Shimmer width="60%" height={10} style={{ marginBottom: 10 }} />
      <Shimmer width="40%" height={22} style={{ marginBottom: 8 }} />
      <Shimmer width="55%" height={10} />
    </div>
  </div>
);

const ShimmerSection = ({ height = 240 }) => (
  <>
    <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", gap: 14 }}>
      <Shimmer width={40} height={40} radius={12} />
      <div style={{ flex: 1 }}>
        <Shimmer width={180} height={14} style={{ marginBottom: 6 }} />
        <Shimmer width={260} height={10} />
      </div>
    </div>
    <div style={{ padding: "0 24px 22px 24px" }}>
      <Shimmer width="100%" height={height} radius={12} />
    </div>
  </>
);

const ShimmerTable = ({ rows = 6, cols = 7 }) => (
  <>
    <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", gap: 14 }}>
      <Shimmer width={40} height={40} radius={12} />
      <div style={{ flex: 1 }}>
        <Shimmer width={180} height={14} style={{ marginBottom: 6 }} />
        <Shimmer width={260} height={10} />
      </div>
    </div>
    <div style={{ padding: "0 24px 22px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Shimmer width={220} height={32} radius={10} />
        <Shimmer width={120} height={20} />
      </div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 16, padding: "12px 16px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} style={{ flex: 1 }}><Shimmer width="60%" height={10} /></div>
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} style={{ display: "flex", gap: 16, padding: "14px 16px", borderBottom: "1px solid #f1f5f9" }}>
            {Array.from({ length: cols }).map((_, c) => (
              <div key={c} style={{ flex: 1 }}><Shimmer width={c === 0 ? "80%" : "55%"} height={12} /></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </>
);

const DashboardShimmer = () => (
  <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
    {/* Header */}
    <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #f1f5f9" }}>
      <Shimmer width={44} height={44} radius={12} />
      <div style={{ flex: 1 }}>
        <Shimmer width={220} height={16} style={{ marginBottom: 6 }} />
        <Shimmer width={320} height={10} />
      </div>
      <Shimmer width={160} height={30} radius={8} />
      <Shimmer width={90} height={30} radius={8} />
    </div>

    {/* Filters */}
    <div style={{ padding: "16px 24px" }}>
      <Row className="g-2">
        {[0, 1, 2].map(i => (
          <Col md={4} sm={6} key={i}>
            <Shimmer width="40%" height={10} style={{ marginBottom: 6 }} />
            <Shimmer width="100%" height={38} radius={10} />
          </Col>
        ))}
      </Row>
    </div>

    <Divider />

    {/* KPIs */}
    <div style={{ padding: "18px 24px 4px 24px" }}>
      <Row>
        {[0, 1, 2, 3].map(i => (
          <Col key={i} lg={3} md={6} sm={12} style={{ marginBottom: 16 }}>
            <ShimmerKpi />
          </Col>
        ))}
      </Row>
    </div>

    <Divider />
    <ShimmerSection height={240} />
    <Divider />
    <ShimmerSection height={240} />
    <Divider />
    <ShimmerTable rows={5} cols={7} />
  </div>
);

const Section = ({ icon, iconColor, iconBg, iconBorder, title, subtitle, action, children }) => (
  <div>
    <div style={{ padding: "18px 24px", display: "flex", alignItems: "center", gap: 14 }}>
      <IconChip icon={icon} color={iconColor} bg={iconBg} border={iconBorder} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h5 style={{ color: THEME.text, fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: "-0.01em" }}>{title}</h5>
        {subtitle && (
          <span style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 500, marginTop: 2 }}>{subtitle}</span>
        )}
      </div>
      {action && <div style={{ marginLeft: "auto" }}>{action}</div>}
    </div>
    <div style={{ padding: "0 24px 22px 24px" }}>{children}</div>
  </div>
);

const STATUS_STYLE = {
  Hot:           { bg: "#fee2e2", color: "#dc2626" },
  Warm:          { bg: "#fef3c7", color: "#d97706" },
  Cold:          { bg: "#dbeafe", color: "#2563eb" },
  Converted:     { bg: "#dcfce7", color: "#059669" },
  Confirmed:     { bg: "#dcfce7", color: "#059669" },
  "Pending Fee": { bg: "#fef3c7", color: "#d97706" },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_STYLE[status] || { bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }}>
      {status}
    </span>
  );
};


// ── Main Dashboard ───────────────────────────────────────────────────────────
class AdmissionsLeadsDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      branches: [],
      getClasses: [],
      branchSearch: [],
      classSearch: [],
      yearSearch: "",
      academic_year_text: "",
      loading: true,

      // Dashboard aggregate (from getAdmissionsDashboard service)
      // Starts empty — populated by fetchDashboard. SAMPLE_DASHBOARD_RESPONSE
      // is kept around purely as a reference for the backend team.
      dashboardData: EMPTY_DASHBOARD_RESPONSE,
      dashboardLoading: false,
      // Default to the same window DateBlock shows on first paint (today-5d → today),
      // so the very first fetch sends date filters instead of an unbounded query.
      dashFromDate: moment().subtract(5, "days").format("YYYY-MM-DD"),
      dashToDate:   moment().format("YYYY-MM-DD"),
    };
  }

  componentDidMount() {
    this.getBranches();
    this.getClasses();
    // Branch comes from the header (NavbarOne) — re-scope when it changes.
    // getBranches resets branchSearch, which componentDidUpdate turns into a refetch.
    this.offBranchScope = onBranchScopeChange(() => this.getBranches());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  componentDidUpdate(prevProps, prevState) {
    const academicYears = this.props.getAcademicYearsQuery?.getAcademicYears || [];
    if (academicYears.length > 0 && !this.state.yearSearch) {
      const now = new Date();
      const startYear = now.getMonth() >= 4 ? now.getFullYear() : now.getFullYear() - 1;
      const shortStr = `${startYear}-${String(startYear + 1).slice(-2)}`;
      const longStr  = `${startYear}-${startYear + 1}`;
      const defaultYear = academicYears.find(y => y.academic_year === shortStr)
        || academicYears.find(y => y.academic_year === longStr)
        || academicYears[0];
      this.setState({ yearSearch: defaultYear.id, academic_year_text: defaultYear.academic_year });
    }

    // Re-fetch dashboard when filters change
    const branchChanged = prevState.branchSearch.length !== this.state.branchSearch.length
      || prevState.branchSearch.some((v, i) => v !== this.state.branchSearch[i]);
    const dateChanged = prevState.dashFromDate !== this.state.dashFromDate
      || prevState.dashToDate !== this.state.dashToDate;
    if (this.state.branches.length > 0 && (branchChanged || dateChanged)) {
      this.fetchDashboard();
    }
  }

  handleDashboardDateRange = (startDate, endDate) => {
    this.setState({
      dashFromDate: startDate.format("YYYY-MM-DD"),
      dashToDate:   endDate.format("YYYY-MM-DD"),
    });
  };

  fetchDashboard = async () => {
    const { branchSearch, dashFromDate, dashToDate } = this.state;
    this.setState({ dashboardLoading: true });

    const variables = {
      institute_id: parseInt(Cookies.get("institutionid")),
    };
    // Service takes branch_id as a comma-separated list of IDs (String).
    // Empty selection → omit the key so backend treats it as all branches.
    if (branchSearch.length > 0) {
      variables.branch_id = branchSearch.join(",");
    }
    if (dashFromDate) variables.fromDate = dashFromDate;
    if (dashToDate)   variables.toDate   = dashToDate;

    try {
      const result = await this.props.client.query({
        query: GET_ADMISSIONS_DASHBOARD,
        variables,
        fetchPolicy: "network-only",
      });
      const data = result.data && result.data.getAdmissionsDashboard;
      this.setState({
        dashboardData: data || EMPTY_DASHBOARD_RESPONSE,
        dashboardLoading: false,
      });
    } catch {
      this.setState({ dashboardData: EMPTY_DASHBOARD_RESPONSE, dashboardLoading: false });
    }
  };

  getBranches = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: parseInt(Cookies.get("institutionid")) },
        fetchPolicy: "network-only",
      });
      // The header's branch selection IS this dashboard's branch filter, and it
      // never widens past the branches the user is authorized for.
      const branches = scopeBranches(result.data.getBranches || []);
      const allBranchIds = branches.map(b => b.id);
      const allClassIds = Array.from(new Set(branches.flatMap(b => b.sections || []).map(s => s.class_id)));
      this.setState({ branches, branchSearch: allBranchIds, classSearch: allClassIds });
    } catch {
      // ignore
    } finally {
      this.setState({ loading: false });
    }
  };

  getClasses = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_CLASSES,
        variables: { id: 1 },
        fetchPolicy: "network-only",
      });
      this.setState({ getClasses: result.data.getExamClasses || [] });
    } catch {
      // ignore
    }
  };

  // Resets back to the header's full branch scope, never wider than that.
  resetFilters = () => {
    const { branches } = this.state;
    const allBranchIds = branches.map(b => b.id);
    const allClassIds = Array.from(new Set(branches.flatMap(b => b.sections || []).map(s => s.class_id)));
    this.setState({ branchSearch: allBranchIds, classSearch: allClassIds });
  };

  enquiriesColumns = [
    { dataField: "sno",       text: "#",      formatter: (_c, _r, i) => i + 1 },
    { dataField: "date",      text: "Date",   sort: true },
    { dataField: "name",      text: "Name",   sort: true },
    { dataField: "phone",     text: "Phone",  sort: true },
    { dataField: "className", text: "Class",  sort: true },
    { dataField: "branch",    text: "Branch", sort: true },
    { dataField: "source",    text: "Source", sort: true },
    { dataField: "status",    text: "Status", sort: true, formatter: (cell) => <StatusPill status={cell} /> },
  ];

  admissionsColumns = [
    { dataField: "sno",       text: "#",       formatter: (_c, _r, i) => i + 1 },
    { dataField: "date",      text: "Date",    sort: true },
    { dataField: "name",      text: "Name",    sort: true },
    { dataField: "phone",     text: "Phone",   sort: true },
    { dataField: "className", text: "Class",   sort: true },
    { dataField: "branch",    text: "Branch",  sort: true },
    { dataField: "rollNo",    text: "Roll No", sort: true },
    { dataField: "status",    text: "Status",  sort: true, formatter: (cell) => <StatusPill status={cell} /> },
  ];


  render() {
    const { branches, getClasses, branchSearch, classSearch, yearSearch, loading } = this.state;
    const academicYears = this.props.getAcademicYearsQuery?.getAcademicYears || [];

    if (loading) {
      return (
        <div style={{ background: THEME.bg, minHeight: "100vh", padding: "16px 0", paddingBottom: 40 }}>
          <style>{`
            * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; }
            .adm-shimmer { position: relative; overflow: hidden; background: #e9edf3; display: block; }
            .adm-shimmer::after {
              content: ""; position: absolute; inset: 0; transform: translateX(-100%);
              background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 50%, rgba(255,255,255,0) 100%);
              animation: adm-shimmer-sweep 1.4s ease-in-out infinite;
            }
            @keyframes adm-shimmer-sweep { 100% { transform: translateX(100%); } }
          `}</style>
          <Container fluid style={{ padding: "0 12px" }}>
            <DashboardShimmer />
          </Container>
        </div>
      );
    }

    const selectedBranchObjs = branchSearch.length > 0
      ? branches.filter(b => branchSearch.map(Number).includes(Number(b.id)))
      : branches;

    const classOptions = Array.from(new Set(selectedBranchObjs.flatMap(b => b.sections || []).map(s => s.class_id)))
      .map(cid => getClasses.find(c => c.id === cid)).filter(Boolean)
      .map(c => ({ value: c.id, label: c.class }));

    // ── All aggregates come from the API response ──
    const dash = this.state.dashboardData || EMPTY_DASHBOARD_RESPONSE;
    const enquiries  = dash.enquiries  || [];
    const admissions = dash.admissions || [];

    const kpiOverrides = {
      enquiry:   dash.kpis?.totalEnquiries,
      lead:      dash.kpis?.totalLeads,
      exam:      dash.kpis?.totalExams,
      admission: dash.kpis?.totalAdmissions,
    };

    const dayWiseData = dash.dayWise || [];
    const classWiseData = dash.classWise || [];
    const branchWiseData = dash.branchWise || [];

    const leadStatusColors = { Hot: THEME.danger, Warm: THEME.warning, Cold: THEME.info, Converted: THEME.success };
    const leadStatusObj = dash.leadStatus || {};
    const leadStatusData = [
      { name: "Hot",       value: leadStatusObj.hot       || 0, color: leadStatusColors.Hot       },
      { name: "Warm",      value: leadStatusObj.warm      || 0, color: leadStatusColors.Warm      },
      { name: "Cold",      value: leadStatusObj.cold      || 0, color: leadStatusColors.Cold      },
      { name: "Converted", value: leadStatusObj.converted || 0, color: leadStatusColors.Converted },
    ].filter(d => d.value > 0);

    const sourceData = (dash.sourceWise || [])
      .map(s => ({ name: s.name, value: s.total }))
      .sort((a, b) => b.value - a.value);

    const admissionStatusObj = dash.admissionStatus || {};
    const admissionStatusData = [
      { name: "Confirmed",   value: admissionStatusObj.confirmed  || 0, color: THEME.success },
      { name: "Pending Fee", value: admissionStatusObj.pendingFee || 0, color: THEME.warning },
    ].filter(d => d.value > 0);

    const totalEnq      = kpiOverrides.enquiry   ?? 0;
    const engagedLeads  = (leadStatusObj.hot || 0) + (leadStatusObj.warm || 0);
    const convertedEnq  = leadStatusObj.converted || 0;
    const totalAdm      = kpiOverrides.admission ?? 0;
    const maxFunnel     = Math.max(totalEnq, totalAdm, 1);
    const funnelData = [
      { stage: "Total Enquiries",   value: totalEnq,     color: THEME.primary, pct: Math.round((totalEnq / maxFunnel) * 100)     },
      { stage: "Engaged (Hot+Warm)", value: engagedLeads, color: THEME.warning, pct: Math.round((engagedLeads / maxFunnel) * 100) },
      { stage: "Converted",          value: convertedEnq, color: THEME.purple,  pct: Math.round((convertedEnq / maxFunnel) * 100) },
      { stage: "Admitted",           value: totalAdm,     color: THEME.success, pct: Math.round((totalAdm / maxFunnel) * 100)     },
    ];

    const branchPerformance = branchWiseData.map(b => ({
      name: b.name,
      enquiries: b.enquiries,
      admissions: b.admissions,
      rate: b.enquiries > 0 ? Math.round((b.admissions / b.enquiries) * 100) : 0,
    })).sort((a, b) => b.rate - a.rate);

    const sourcePerformance = (dash.sourceWise || [])
      .map(s => ({
        name: s.name,
        total: s.total,
        converted: s.converted,
        rate: s.total > 0 ? Math.round((s.converted / s.total) * 100) : 0,
      }))
      .filter(r => r.total > 0)
      .sort((a, b) => b.rate - a.rate);

    const weekdayData = (dash.weekdayDistribution || []).map(w => ({
      name: w.day,
      enquiries: w.enquiries,
      admissions: w.admissions,
    }));

    let runningTotal = 0;
    const cumulativeData = dayWiseData.map(d => {
      runningTotal += d.admissions;
      return { date: d.date, cumulative: runningTotal };
    });

    return (
      <div style={{ background: THEME.bg, minHeight: "100vh", padding: "16px 0", paddingBottom: 40 }}>
        <style>{`
          * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; }
        `}</style>

        <Container fluid style={{ padding: "0 12px" }}>
          {/* Single master card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: THEME.cardShadow,
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
              <IconChip icon="fal fa-user-graduate" size={44} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <h5 style={{ color: THEME.text, fontWeight: 700, fontSize: 18, margin: 0, letterSpacing: "-0.01em" }}>
                  Admissions Dashboard
                </h5>
                <span style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 500, marginTop: 2 }}>
                  Enquiries, lead tracking, exams and admissions overview
                </span>
              </div>
              <button
                type="button"
                onClick={this.resetFilters}
                style={{
                  marginLeft: "auto",
                  background: "#f5f3ff",
                  color: "#7c3aed",
                  border: "1px solid #ddd6fe",
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <i className="fal fa-redo" style={{ marginRight: 6 }} />
                Reset
              </button>
            </div>
            <div style={{ padding: "16px 24px" }}>
              <Row className="g-2">
                {/* No Branch filter — the header's branch selection scopes this dashboard. */}
                <Col md={6} sm={6}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4, display: "block" }}>
                    Date Range
                  </label>
                  <div className="adm-dash-daterange">
                    <DateBlock
                      applyCallback={this.handleDashboardDateRange}
                      initialStart={this.state.dashFromDate}
                      initialEnd={this.state.dashToDate}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <Divider />

            {/* KPI Row */}
            <div style={{ padding: "18px 24px 4px 24px" }}>
              <Row>
                {KPI_DATA.map((kpi) => (
                  <Col key={kpi.key} lg={3} md={6} sm={12} style={{ marginBottom: 16 }}>
                    <KpiCard kpi={{ ...kpi, value: kpiOverrides[kpi.key] ?? kpi.value }} />
                  </Col>
                ))}
              </Row>
            </div>

            <Divider />

            {/* Day-wise trend */}
            <Section
              icon="fal fa-chart-line"
              iconColor="#3b82f6"
              iconBg="#dbeafe"
              iconBorder="#bfdbfe"
              title="Day-wise Trend"
              subtitle="Enquiries vs admissions by day"
            >
              {dayWiseData.length === 0 ? (
                <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>
                  No data for the current filters
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={dayWiseData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                    <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="enquiries"  stroke={THEME.primary} strokeWidth={2.5} dot={{ r: 3 }} name="Enquiries"  />
                    <Line type="monotone" dataKey="admissions" stroke={THEME.success} strokeWidth={2.5} dot={{ r: 3 }} name="Admissions" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Divider />

            {/* Class & Branch wise */}
            <Section
              icon="fal fa-chalkboard-teacher"
              iconColor="#7c3aed"
              iconBg="#f5f3ff"
              iconBorder="#ddd6fe"
              title="Class & Branch Wise"
              subtitle="Enquiries and admissions broken down by class and by branch"
            >
              <Row>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Class Wise</div>
                  {classWiseData.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={classWiseData} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                        <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="enquiries"  fill={THEME.primary} radius={[6, 6, 0, 0]} name="Enquiries"  />
                        <Bar dataKey="admissions" fill={THEME.success} radius={[6, 6, 0, 0]} name="Admissions" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Branch Wise</div>
                  {branchWiseData.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={branchWiseData} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                        <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="enquiries"  fill={THEME.primary} radius={[6, 6, 0, 0]} name="Enquiries"  />
                        <Bar dataKey="admissions" fill={THEME.success} radius={[6, 6, 0, 0]} name="Admissions" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
              </Row>
            </Section>

            <Divider />

            {/* Lead status pie */}
            <Section
              icon="fal fa-tasks"
              iconColor="#7c3aed"
              iconBg="#ede9fe"
              iconBorder="#ddd6fe"
              title="Lead Status"
              subtitle="Enquiry status distribution"
            >
              {leadStatusData.length === 0 ? (
                <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No leads</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={leadStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={95}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ value }) => `${value}`}
                        labelLine={false}
                      >
                        {leadStatusData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
                    {leadStatusData.map((s) => (
                      <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: "inline-block" }} />
                        <span style={{ fontSize: 12, color: THEME.textSecondary, fontWeight: 600 }}>
                          {s.name} <span style={{ color: THEME.textMuted, fontWeight: 500 }}>({s.value})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Section>

            <Divider />

            {/* Source-wise + Admission status */}
            <Section
              icon="fal fa-stream"
              iconColor="#06b6d4"
              iconBg="#ecfeff"
              iconBorder="#a5f3fc"
              title="Source & Admission Status"
              subtitle="Where enquiries come from and how admissions are settling"
            >
              <Row>
                <Col lg={7} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Enquiry Source</div>
                  {sourceData.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={sourceData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} width={100} />
                        <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                        <Bar dataKey="value" fill={THEME.info} radius={[0, 6, 6, 0]} name="Enquiries" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
                <Col lg={5} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Admission Status</div>
                  {admissionStatusData.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No admissions</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={admissionStatusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                            label={({ value }) => `${value}`}
                            labelLine={false}
                          >
                            {admissionStatusData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
                        {admissionStatusData.map((s) => (
                          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: "inline-block" }} />
                            <span style={{ fontSize: 12, color: THEME.textSecondary, fontWeight: 600 }}>
                              {s.name} <span style={{ color: THEME.textMuted, fontWeight: 500 }}>({s.value})</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </Section>

            <Divider />

            {/* Conversion Funnel */}
            <Section
              icon="fal fa-funnel-dollar"
              iconColor="#7c3aed"
              iconBg="#f5f3ff"
              iconBorder="#ddd6fe"
              title="Conversion Funnel"
              subtitle="Where leads progress and drop off through the pipeline"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {funnelData.map((stage, i) => {
                  const prev = i === 0 ? null : funnelData[i - 1];
                  const dropPct = prev && prev.value > 0
                    ? Math.round(((prev.value - stage.value) / prev.value) * 100)
                    : null;
                  const convFromTop = funnelData[0].value > 0
                    ? Math.round((stage.value / funnelData[0].value) * 100)
                    : 0;
                  return (
                    <div key={stage.stage} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ minWidth: 180, fontSize: 13, fontWeight: 600, color: THEME.text }}>
                        {stage.stage}
                      </div>
                      <div style={{ flex: 1, height: 32, background: THEME.borderLight, borderRadius: 8, overflow: "hidden", position: "relative" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${stage.pct}%`,
                            background: `linear-gradient(90deg, ${stage.color} 0%, ${stage.color}cc 100%)`,
                            borderRadius: 8,
                            transition: "width 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingRight: 10,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {stage.value}
                        </div>
                      </div>
                      <div style={{ minWidth: 130, fontSize: 12, color: THEME.textMuted, fontWeight: 600, textAlign: "right" }}>
                        <span style={{ color: stage.color, fontWeight: 700 }}>{convFromTop}%</span> of top
                        {dropPct !== null && dropPct > 0 && (
                          <span style={{ color: THEME.danger, marginLeft: 8 }}>↓ {dropPct}%</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Divider />

            {/* Conversion Rates */}
            <Section
              icon="fal fa-percentage"
              iconColor="#10b981"
              iconBg="#dcfce7"
              iconBorder="#bbf7d0"
              title="Conversion Rates"
              subtitle="Admission rate by branch and lead-to-conversion rate by source"
            >
              <Row>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Branch — admissions ÷ enquiries
                  </div>
                  {branchPerformance.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={branchPerformance}>
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                        <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} unit="%" />
                        <Tooltip
                          contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }}
                          formatter={(value, name, props) => {
                            if (name === "Conversion %") return [`${value}%`, name];
                            return [value, name];
                          }}
                          labelFormatter={(label) => {
                            const row = branchPerformance.find(r => r.name === label);
                            return row ? `${label} — ${row.admissions} of ${row.enquiries}` : label;
                          }}
                        />
                        <Bar dataKey="rate" fill={THEME.success} radius={[6, 6, 0, 0]} name="Conversion %" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Source — converted ÷ total enquiries
                  </div>
                  {sourcePerformance.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={sourcePerformance}>
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                        <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} unit="%" />
                        <Tooltip
                          contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }}
                          formatter={(value) => [`${value}%`, "Conversion %"]}
                          labelFormatter={(label) => {
                            const row = sourcePerformance.find(r => r.name === label);
                            return row ? `${label} — ${row.converted} of ${row.total}` : label;
                          }}
                        />
                        <Bar dataKey="rate" fill={THEME.purple} radius={[6, 6, 0, 0]} name="Conversion %" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Col>
              </Row>
            </Section>

            <Divider />

            {/* Activity Patterns */}
            <Section
              icon="fal fa-analytics"
              iconColor="#ec4899"
              iconBg="#fdf2f8"
              iconBorder="#fbcfe8"
              title="Activity Patterns"
              subtitle="When activity happens and how admissions accumulate over time"
            >
              <Row>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Weekday Distribution
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={weekdayData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                      <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="enquiries"  fill={THEME.primary} radius={[6, 6, 0, 0]} name="Enquiries"  />
                      <Bar dataKey="admissions" fill={THEME.success} radius={[6, 6, 0, 0]} name="Admissions" />
                    </BarChart>
                  </ResponsiveContainer>
                </Col>
                <Col lg={6} md={12}>
                  <div style={{ fontSize: 12, color: THEME.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                    Cumulative Admissions
                  </div>
                  {cumulativeData.length === 0 ? (
                    <div style={{ color: THEME.textMuted, fontSize: 13, padding: "24px 0", textAlign: "center" }}>No data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={cumulativeData}>
                        <defs>
                          <linearGradient id="cumulFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor={THEME.success} stopOpacity={0.4} />
                            <stop offset="100%" stopColor={THEME.success} stopOpacity={0}   />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={THEME.borderLight} vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} />
                        <YAxis tick={{ fontSize: 12, fill: THEME.textMuted }} axisLine={{ stroke: THEME.border }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: `1px solid ${THEME.border}`, borderRadius: 10, fontSize: 12 }} />
                        <Line type="monotone" dataKey="cumulative" stroke={THEME.success} strokeWidth={2.5} dot={{ r: 3 }} fill="url(#cumulFill)" name="Cumulative" />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Col>
              </Row>
            </Section>

            <Divider />

            {/* Enquiries table */}
            <Section
              icon="fal fa-file-alt"
              iconColor="#3b82f6"
              iconBg="#dbeafe"
              iconBorder="#bfdbfe"
              title="Enquiries"
              subtitle="Recent admission enquiries"
              action={
                <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 20, padding: "4px 12px", fontWeight: 600, fontSize: 12, border: "1px solid #bfdbfe" }}>
                  {enquiries.length} enquiries
                </span>
              }
            >
              <CardLessDataTable
                parentData={enquiries}
                particlecolumns={this.enquiriesColumns}
                name="admissions_enquiries"
                placeholder="Search enquiries"
              />
            </Section>

            <Divider />

            {/* Admissions table */}
            <Section
              icon="fal fa-user-graduate"
              iconColor="#10b981"
              iconBg="#dcfce7"
              iconBorder="#bbf7d0"
              title="Admissions"
              subtitle="Recent confirmed and pending admissions"
              action={
                <span style={{ background: "#dcfce7", color: "#059669", borderRadius: 20, padding: "4px 12px", fontWeight: 600, fontSize: 12, border: "1px solid #bbf7d0" }}>
                  {admissions.length} admissions
                </span>
              }
            >
              <CardLessDataTable
                parentData={admissions}
                particlecolumns={this.admissionsColumns}
                name="admissions_list"
                placeholder="Search admissions"
              />
            </Section>
          </div>
        </Container>
      </div>
    );
  }
}

export default compose(
  withRouter,
  withApollo,
  graphql(GET_ACADEMIC_YEARS, {
    name: "getAcademicYearsQuery",
    options: { variables: { id: 1 }, fetchPolicy: "network-only" },
  }),
)(AdmissionsLeadsDashboard);
